import { cn } from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import {
  ArrowDownToLine,
  CreditCard,
  PackageSearch,
  ScanBarcodeIcon,
} from "lucide-react";
import type { ShopFeatureFlags } from "@/modules/merchant/domain/contracts/shop-feature-flags";
import { canUseMerchantPermission } from "@/modules/merchant/presentation/api/client";
import { hasShopFeature } from "@/modules/merchant/presentation/lib/has-shop-feature";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";

const POS_ROUTE = "/app/shops/$shopId/pos" as const;
const CATALOG_ROUTE = "/app/shops/$shopId/catalog" as const;
const INVENTORY_RECEIVE_ROUTE =
  "/app/shops/$shopId/inventory/receive" as const;

type ShopDashboardQuickActionsProps = {
  shopId: string | undefined;
  permissions: string[];
  verifiedLevel: string | null;
  featureFlags: ShopFeatureFlags | null | undefined;
};

type QuickAction = {
  key: string;
  label: string;
  hint: string;
  to: string;
  search?: Record<string, string>;
  icon: typeof CreditCard;
  className: string;
};

export function ShopDashboardQuickActions({
  shopId,
  permissions,
  verifiedLevel,
  featureFlags,
}: ShopDashboardQuickActionsProps) {
  if (!shopId) return null;

  const canPos = canUseMerchantPermission(
    permissions,
    MerchantPermissions.pos.read,
    verifiedLevel,
  );
  const canCatalog = canUseMerchantPermission(
    permissions,
    MerchantPermissions.catalog.read,
    verifiedLevel,
  );
  const canReceive =
    hasShopFeature(featureFlags, "inventory") &&
    canUseMerchantPermission(
      permissions,
      MerchantPermissions.inventory.receive,
      verifiedLevel,
    );

  const actions: QuickAction[] = [];

  if (canPos) {
    actions.push(
      {
        key: "sell",
        label: "ຂາຍ",
        hint: "ເປີດໜ້າຂາຍ",
        to: POS_ROUTE,
        icon: CreditCard,
        className:
          "border-sky-500/30 bg-sky-500/10 text-sky-900 hover:bg-sky-500/15 dark:text-sky-100",
      },
      {
        key: "scan",
        label: "ສະແກນ",
        hint: "ສະແກນ barcode",
        to: POS_ROUTE,
        search: { focus: "scan" },
        icon: ScanBarcodeIcon,
        className:
          "border-emerald-500/30 bg-emerald-500/10 text-emerald-900 hover:bg-emerald-500/15 dark:text-emerald-100",
      },
    );
  }

  if (canReceive) {
    actions.push({
      key: "receive",
      label: "ຮັບເຂົ້າສາງ",
      hint: "ບັນທຶກສິນຄ້າເຂົ້າ",
      to: INVENTORY_RECEIVE_ROUTE,
      icon: ArrowDownToLine,
      className:
        "border-amber-500/30 bg-amber-500/10 text-amber-950 hover:bg-amber-500/15 dark:text-amber-100",
    });
  }

  if (canCatalog) {
    actions.push({
      key: "search",
      label: "ຊອກສິນຄ້າ",
      hint: "ຄົ້ນລາຍການສິນຄ້າ",
      to: CATALOG_ROUTE,
      icon: PackageSearch,
      className:
        "border-violet-500/30 bg-violet-500/10 text-violet-950 hover:bg-violet-500/15 dark:text-violet-100",
    });
  }

  if (actions.length === 0) return null;

  return (
    <section
      aria-labelledby="shop-dashboard-quick-actions"
      className="mb-8"
    >
      <h2
        id="shop-dashboard-quick-actions"
        className="font-heading font-semibold text-foreground text-sm"
      >
        ວຽກດ່ວນ
      </h2>
      <p className="mt-0.5 text-muted-foreground text-xs">
        ຂາຍ · ສະແກນ · ຮັບເຂົ້າ · ຊອກສິນຄ້າ
      </p>
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {actions.map((action) => {
          const Icon = action.icon;
          return (
            <Link
              key={action.key}
              to={action.to}
              params={{ shopId }}
              search={action.search}
              className={cn(
                "group flex min-h-[5.5rem] flex-col items-center justify-center gap-2 rounded-2xl border-2 px-3 py-4 text-center shadow-sm transition-all touch-manipulation hover:shadow-md",
                action.className,
              )}
            >
              <Icon className="size-7 shrink-0" aria-hidden />
              <span className="font-bold text-sm leading-tight">
                {action.label}
              </span>
              <span className="text-[10px] opacity-80 leading-snug">
                {action.hint}
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
